import { useEffect, useState } from "react";
import { Save, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const sectionConfig = {
  explorations: {
    title: "Exploration",
    endpoint: "explorations",
    statuses: ["active", "paused", "completed", "someday"],
    descriptionLabel: "What are you exploring?",
    descriptionKey: "description",
    hasNextStep: true,
  },
  knowledge: {
    title: "Knowledge entry",
    endpoint: "knowledge",
    statuses: ["insight", "observation", "lesson", "decision", "note", "resource"],
    descriptionLabel: "What did you learn or observe?",
    descriptionKey: "content",
    typeKey: "entry_type",
    hasSource: true,
  },
  ideas: {
    title: "Idea",
    endpoint: "ideas",
    statuses: ["captured", "exploring", "testing", "developing", "parked"],
    descriptionLabel: "Describe the possibility",
    descriptionKey: "description",
    hasNextStep: true,
  },
  questions: {
    title: "Question",
    endpoint: "questions",
    statuses: ["open", "exploring", "answered", "parked"],
    descriptionLabel: "Why is this worth understanding?",
    descriptionKey: "context",
    hasAnswer: true,
  },
};

const blankRecord = (section) => {
  const config = sectionConfig[section];
  return {
    title: "",
    [config.descriptionKey]: "",
    [config.typeKey || "status"]: config.statuses[0],
    ...(config.typeKey ? { status: undefined } : {}),
    tags: [],
    exploration_ids: [],
    next_step: "",
    source_label: "",
    source_url: "",
    answer_summary: "",
  };
};

const asTags = (value) =>
  value
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean);

export const OwnerLabEditorDialog = ({
  explorations,
  item,
  onDelete,
  onOpenChange,
  onSave,
  open,
  section,
}) => {
  const config = sectionConfig[section];
  const [form, setForm] = useState(blankRecord(section));
  const [saving, setSaving] = useState(false);
  const isNew = !item?.id;

  useEffect(() => {
    setForm({ ...blankRecord(section), ...(item || {}) });
  }, [item, open, section]);

  const setValue = (key, value) =>
    setForm((current) => ({ ...current, [key]: value }));

  const toggleExploration = (explorationId) => {
    setForm((current) => ({
      ...current,
      exploration_ids: current.exploration_ids.includes(explorationId)
        ? current.exploration_ids.filter((id) => id !== explorationId)
        : [...current.exploration_ids, explorationId],
    }));
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        tags: Array.isArray(form.tags) ? form.tags : asTags(form.tags || ""),
      };
      if (config.typeKey) {
        payload[config.typeKey] = form[config.typeKey] || config.statuses[0];
        delete payload.status;
      }
      await onSave(section, item?.id, payload);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!item?.id || !window.confirm(`Delete this ${config.title.toLowerCase()}?`)) {
      return;
    }
    setSaving(true);
    try {
      await onDelete(section, item.id);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="owner-lab-dialog" data-testid="owner-lab-editor-dialog">
        <DialogHeader>
          <p className="owner-lab-dialog-kicker">Private owner workspace</p>
          <DialogTitle data-testid="owner-lab-editor-title">
            {isNew ? `New ${config.title}` : `Edit ${config.title}`}
          </DialogTitle>
          <DialogDescription>
            Capture freely. Owner Lab is private to you and never changes an operational project, schedule, or invoice.
          </DialogDescription>
        </DialogHeader>
        <form data-testid={`owner-lab-${section}-form`} onSubmit={save}>
          <div className="owner-lab-editor-grid">
            <label className="owner-lab-editor-wide" htmlFor="owner-lab-title-input">
              Title
              <input
                data-testid="owner-lab-title-input"
                id="owner-lab-title-input"
                onChange={(event) => setValue("title", event.target.value)}
                required
                value={form.title}
              />
            </label>
            <label htmlFor="owner-lab-status-select">
              {config.typeKey ? "Kind" : "Status"}
              <select
                data-testid="owner-lab-status-select"
                id="owner-lab-status-select"
                onChange={(event) =>
                  setValue(config.typeKey || "status", event.target.value)
                }
                value={form[config.typeKey || "status"] || config.statuses[0]}
              >
                {config.statuses.map((value) => (
                  <option key={value} value={value}>
                    {value.replaceAll("_", " ")}
                  </option>
                ))}
              </select>
            </label>
            <label htmlFor="owner-lab-tags-input">
              Concepts / tags
              <input
                data-testid="owner-lab-tags-input"
                id="owner-lab-tags-input"
                onChange={(event) => setValue("tags", asTags(event.target.value))}
                placeholder="pricing, incentives, restaurant"
                value={(form.tags || []).join(", ")}
              />
            </label>
            <label className="owner-lab-editor-wide" htmlFor="owner-lab-content-input">
              {config.descriptionLabel}
              <textarea
                data-testid="owner-lab-content-input"
                id="owner-lab-content-input"
                onChange={(event) =>
                  setValue(config.descriptionKey, event.target.value)
                }
                required={section === "knowledge"}
                value={form[config.descriptionKey] || ""}
              />
            </label>
            {config.hasNextStep && (
              <label className="owner-lab-editor-wide" htmlFor="owner-lab-next-step-input">
                Next step
                <input
                  data-testid="owner-lab-next-step-input"
                  id="owner-lab-next-step-input"
                  onChange={(event) => setValue("next_step", event.target.value)}
                  placeholder="Optional — a tiny action is enough"
                  value={form.next_step || ""}
                />
              </label>
            )}
            {config.hasSource && (
              <>
                <label htmlFor="owner-lab-source-label-input">
                  Source label
                  <input
                    data-testid="owner-lab-source-label-input"
                    id="owner-lab-source-label-input"
                    onChange={(event) => setValue("source_label", event.target.value)}
                    placeholder="Book, course, conversation"
                    value={form.source_label || ""}
                  />
                </label>
                <label htmlFor="owner-lab-source-url-input">
                  Source URL
                  <input
                    data-testid="owner-lab-source-url-input"
                    id="owner-lab-source-url-input"
                    onChange={(event) => setValue("source_url", event.target.value)}
                    placeholder="https://"
                    type="url"
                    value={form.source_url || ""}
                  />
                </label>
              </>
            )}
            {config.hasAnswer && (
              <label className="owner-lab-editor-wide" htmlFor="owner-lab-answer-input">
                Current answer or working conclusion
                <textarea
                  data-testid="owner-lab-answer-input"
                  id="owner-lab-answer-input"
                  onChange={(event) => setValue("answer_summary", event.target.value)}
                  value={form.answer_summary || ""}
                />
              </label>
            )}
          </div>
          {section !== "explorations" && (
            <section className="owner-lab-exploration-links" data-testid="owner-lab-exploration-links">
              <strong>Link explorations</strong>
              {explorations.length ? (
                <div>
                  {explorations.map((exploration) => (
                    <label key={exploration.id}>
                      <input
                        checked={form.exploration_ids.includes(exploration.id)}
                        data-testid={`owner-lab-link-exploration-${exploration.id}`}
                        onChange={() => toggleExploration(exploration.id)}
                        type="checkbox"
                      />
                      {exploration.title}
                    </label>
                  ))}
                </div>
              ) : (
                <p>Create an exploration when a note needs a home. It is optional.</p>
              )}
            </section>
          )}
          <DialogFooter>
            {!isNew && (
              <Button
                data-testid="owner-lab-delete-button"
                disabled={saving}
                onClick={remove}
                type="button"
                variant="outline"
              >
                <Trash2 size={15} />
                Delete
              </Button>
            )}
            <Button data-testid="owner-lab-save-button" disabled={saving} type="submit">
              <Save size={15} />
              {saving ? "Saving…" : isNew ? "Add to Lab" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};